import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext, useMemo, useRef } from 'react';
import { perPage } from '~/constants/vdp/ownerReviews';
import StarRatingOwnerReviewContext from '~/context/vdp/StarRatingOwnerReviewContext';
import {
  useGetVehicleDetails,
  useIsNewVehicle
} from '~/context/vdp/VehicleContext';
import type { OwnerReviewsRequest } from '~/services/vdp/contracts';
import fetchReviews from '~/services/vdp/fetchReviews';
import { generateErrorLogger } from '~/utilities/loggers';
import calculateRatingsAndDistribution from '~/utilities/vdp/calculateRatingsAndDistribution';
import useGetApplyReviewsRules from '../useGetApplyReviewsRules';
/**
 * Note: Due to how react-hooks/exhaustive-deps treats object properties, we
 * use `obj.fn.call(undefined, args)` in the code below. Destructuring also
 * solves the problem, but explodes the number of constants in scope, which is
 * especially annoying when the constants all have the same name. Hopefully
 * the lint rule will get an option added to it one day.
 *
 * See https://github.com/facebook/react/issues/16265
 */
const useGetReviews = () => {
  const {
    drawerReviews,
    hasOwnerReviews: {
      setData: setHasOwnerReviews,
      setError: setHasOwnerReviewsError,
      setLoading: setHasOwnerReviewsLoading,
      setLoaded: setHasOwnerReviewsLoaded
    },
    pageNumber,
    pageReviews,
    ratingData,
    searchTerm,
    starRatingFilter,
    setDrawerReviewCount,
    setPageReviewCount
  } = useContext(StarRatingOwnerReviewContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const isInitialRender = useRef(true);
  const { make = '', model = '', trim = '', year = 0 } = useGetVehicleDetails();
  const isNewVehicle = useIsNewVehicle();

  const request = useMemo<OwnerReviewsRequest>(
    () => ({
      make,
      model,
      pageNumber,
      perPage,
      searchTerm,
      starRating: starRatingFilter,
      trim,
      year
    }),
    [make, model, pageNumber, searchTerm, starRatingFilter, trim, year]
  );

  const applyRules = useGetApplyReviewsRules(searchTerm);

  useEffectWithController(
    controller => {
      if (isNewVehicle) {
        setHasOwnerReviewsLoaded();
        ratingData.setLoaded.call(undefined);
        pageReviews.setLoaded.call(undefined);

        return;
      }

      drawerReviews.setLoading.call(undefined);

      if (isInitialRender.current) {
        pageReviews.setLoading.call(undefined);
        ratingData.setLoading.call(undefined);
        setHasOwnerReviewsLoading();
      }

      fetchReviews(request, controller)
        .then(response => {
          const reviews = applyRules(response.data.reviews);

          setDrawerReviewCount(response.data.summary.review_count_total);

          if (request.pageNumber > 1) {
            drawerReviews.setData.call(undefined, v => [...v, ...reviews]);
          } else {
            drawerReviews.setData.call(undefined, reviews);
          }

          if (isInitialRender.current) {
            const ratingDistributions = calculateRatingsAndDistribution(
              response.data.summary.rating_counts,
              reviews
            );

            setPageReviewCount(response.data.summary.review_count_total);
            setHasOwnerReviews(ratingDistributions.totalRatingCounts > 2);
            ratingData.setData.call(undefined, ratingDistributions);
            pageReviews.setData.call(undefined, reviews.slice(0, 4));
          }
        })
        .catch(
          generateErrorLogger(
            'Fetch owner reviews',
            {},
            {
              callback: (err: Error) => {
                trackEvent('Owner Reviews API failed');
                drawerReviews.setError.call(undefined, err);
                if (isInitialRender.current) {
                  pageReviews.setError.call(undefined, err);
                  ratingData.setError.call(undefined, err);
                  setHasOwnerReviewsError(err);
                }
              }
            }
          )
        )
        .finally(() => {
          isInitialRender.current = false;
        });
    },
    [
      applyRules,
      drawerReviews.setData,
      drawerReviews.setError,
      drawerReviews.setLoading,
      isNewVehicle,
      pageReviews.setData,
      pageReviews.setError,
      pageReviews.setLoaded,
      pageReviews.setLoading,
      ratingData.setData,
      ratingData.setError,
      ratingData.setLoaded,
      ratingData.setLoading,
      request,
      setDrawerReviewCount,
      setHasOwnerReviews,
      setHasOwnerReviewsError,
      setHasOwnerReviewsLoaded,
      setHasOwnerReviewsLoading,
      setPageReviewCount,
      trackEvent
    ]
  );
};

export default useGetReviews;
